import { useSearchParams } from 'react-router';
import { useBackgroundQuery, useSuspenseQuery } from '@apollo/client/react';

import { BillingDocument } from 'src/graphql/actions/billing/billing.query';
import { CompanyDocument } from 'src/graphql/actions/company/company.query';
import {
  BillingInvoicesContainer,
  BillingInformationContainer,
  BillingSubscriptionsContainer,
  BillingPaymentContainer,
  BillingCancellationPlanContainer,
} from 'src/containers/Billing';
import { BillingCard } from 'src/components/Billing';
import { withSuspense } from 'src/hoc';
import { CancellationPlanContextProvider } from 'src/context/billing';
import { usePageMeta } from 'src/hooks';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { ExploreSolutionsAlert, BillingNoAccessState } from './ui';

const tabKeys = ['plan-details', 'payment-info'] as const;

const BillingPage = () => {
  usePageMeta('Billing');
  const {
    data: { company },
    refetch: refetchCompany,
  } = useSuspenseQuery(CompanyDocument);
  const [queryRef, { refetch: refetchBilling }] = useBackgroundQuery(BillingDocument);
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('t') as (typeof tabKeys)[number];
  const activeTab = company.policy.readBilling && tabKeys.includes(tabParam) ? tabParam : 'plan-details';

  const refetch = () => {
    Promise.all([refetchCompany(), refetchBilling()]);
  };

  const onValueChange = (key: string) => setSearchParams({ t: key });

  return (
    <div className="container mx-auto">
      <Tabs className="gap-6" value={activeTab} onValueChange={onValueChange}>
        <TabsList className="w-full justify-start">
          <TabsTrigger disabled={!company.policy.readBilling} value={tabKeys[0]} className="flex-0">
            Plan details
          </TabsTrigger>
          <TabsTrigger disabled={!company.policy.readBilling} value={tabKeys[1]} className="flex-0">
            Payment info
          </TabsTrigger>
        </TabsList>
        <TabsContent value={tabKeys[0]}>
          <ExploreSolutionsAlert />
          {company.policy.readBilling ? (
            <CancellationPlanContextProvider
              exclusiveOfferApplied={Boolean(company.subscriptionExclusiveCouponAppliedAt)}
            >
              <BillingSubscriptionsContainer
                queryRef={queryRef}
                cancellationRequested={Boolean(company.subscriptionCancellationRequestedAt)}
              />
              <BillingCancellationPlanContainer refetch={refetch} />
            </CancellationPlanContextProvider>
          ) : (
            <BillingNoAccessState />
          )}
        </TabsContent>
        <TabsContent value={tabKeys[1]}>
          <BillingPaymentContainer queryRef={queryRef} />
          <BillingInformationContainer queryRef={queryRef} />
          <BillingCard title="Invoices" description="View and access your invoice history here.">
            <BillingInvoicesContainer />
          </BillingCard>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default withSuspense(BillingPage);
